import {
  Box,
  BoxProps,
  HStack,
  Heading,
  HeadingProps,
  Image,
  LinkBox,
  LinkBoxProps,
  LinkOverlay,
  StackProps,
  Text,
  chakra
} from "@chakra-ui/react"
import Link, {
  LinkProps,
  LinkTo
} from "@pathwright/ux/src/components/link/Link"
import { forwardRef } from "react"
import Pathicon from "../../../pathicon/Pathicon"
import type { PathCardClickTargetProps } from "./types"

type PathCardProps = LinkBoxProps

const PathCard = forwardRef<HTMLDivElement, PathCardProps>((props, ref) => {
  return (
    <LinkBox
      ref={ref}
      w="100%"
      borderRadius="card"
      shadow="base"
      transform="scale(1.00)"
      transition="transform .2s ease-out"
      _hover={{ shadow: "xl", transform: "scale(1.01)" }}
      css={{
        [`&:has(.OptionMenu button[aria-expanded='true'])`]: {
          shadow: "xl",
          transform: "scale(1.01)",
          // Conditionally layer the PathCard above siblings when it contains
          // an expanded OptionMenu, ensuring the menu is not overlaid by other
          // PathCards.
          pos: "relative",
          zIndex: 10
        }
      }}
      {...props}
    />
  )
})

export function PathCardClickTarget({
  name,
  onClick,
  ...ariaProps
}: {
  name: string
} & PathCardClickTargetProps) {
  return (
    <chakra.button
      type="button"
      aria-label={name}
      {...ariaProps}
      onClick={onClick}
      pos="absolute"
      inset={0}
      zIndex={1}
      cursor="pointer"
      borderRadius="md"
      focusVisibleRing="inside"
    />
  )
}

type PathCardContentProps = StackProps

export function PathCardContent(props: PathCardContentProps) {
  return (
    <HStack
      w="full"
      // May be asked to align right-hand content to top.
      // alignItems="flex-start"
      px={4}
      py={6}
      gap={4}
      pos="relative"
      {...props}
    />
  )
}

type PathCardImageProps = {
  title: string
  image: string
}

export function PathCardImage({ title, image }: PathCardImageProps) {
  return (
    <Image
      src={image}
      alt={title}
      display="block"
      flexShrink={0}
      borderRadius="card"
      maxWidth="100px"
      pos="relative"
    />
  )
}

export type PathCardBackgroundProps = {
  backgroundImage?: string
  backgroundImageOverlay?: string | null
  backgroundOverlay?: string | null
} & BoxProps

export const PathCardBackground = ({
  backgroundImage,
  backgroundImageOverlay,
  backgroundOverlay,
  ...boxProps
}: PathCardBackgroundProps) => {
  return (
    <Box
      pos="absolute"
      inset={0}
      borderRadius="card"
      overflow="hidden"
      {...boxProps}
    >
      <Box
        pos="absolute"
        inset={0}
        transform="scale(1.00)"
        transition="transform 8s ease-out"
        _groupHover={{
          "@media (hover: hover)": {
            transform: "scale(1.2)"
          }
        }}
      >
        <Box
          pos="absolute"
          inset={0}
          backgroundImage={`url(${backgroundImage})`}
          backgroundPosition="center"
          backgroundSize="cover"
          backgroundRepeat="no-repeat"
        />
        {!!backgroundImageOverlay && (
          <Box
            pos="absolute"
            inset={0}
            backgroundImage={backgroundImageOverlay}
            backgroundPosition="center"
            backgroundSize="cover"
            backgroundRepeat="no-repeat"
            opacity={0.0} // try without the overlay for now.
          />
        )}
        {!!backgroundOverlay && (
          <Box pos="absolute" inset={0} bg={`rgba(${backgroundOverlay})`} />
        )}
        <Box pos="absolute" inset={0} bg="blackAlpha.700" />
      </Box>
    </Box>
  )
}

type PathCardTitleProps = {
  link?: LinkTo
  inlineLink?: boolean
} & HeadingProps

export function PathCardTitle({
  link,
  inlineLink,
  children,
  ...headingProps
}: PathCardTitleProps) {
  const heading = (
    <Heading
      as="h2"
      className="theme-heading theme-heading-bold"
      size="xl"
      color="whiteAlpha.900"
      fontWeight="bold"
      lineClamp={2}
      m={0}
      {...headingProps}
    >
      {link && inlineLink ? (
        <Link
          to={link}
          color="inherit"
          pos="relative"
          zIndex={2}
          variant="plain"
        >
          {children}
        </Link>
      ) : (
        children
      )}
    </Heading>
  )

  return link && !inlineLink ? (
    <Link asChild to={link}>
      <LinkOverlay
        className="PathCard__link-overlay"
        w="100%"
        textDecoration="none"
      >
        {heading}
      </LinkOverlay>
    </Link>
  ) : (
    heading
  )
}

type PathCardSubtitleProps = HeadingProps

export function PathCardSubtitle(headingProps: PathCardSubtitleProps) {
  return (
    <Heading
      as="h3"
      size="md"
      fontWeight="medium"
      color="whiteAlpha.900"
      m={0}
      {...headingProps}
    />
  )
}

type PathCardCertificateProps = {
  link?: string
} & LinkProps

export function PathCardCertificate({
  link,
  ...linkProps
}: PathCardCertificateProps) {
  return link ? (
    <Link
      py={1}
      px={4}
      _hover={{
        textDecoration: "none",
        bg: "blackAlpha.600",
        shadow: "md"
      }}
      textDecoration="none"
      color="whiteAlpha.900"
      href={link}
      download
      {...linkProps}
    >
      <HStack>
        <Pathicon icon="certificate-fill" />
        <Text m={0} fontSize="sm" fontWeight="bold" color="whiteAlpha.900">
          Certificate
        </Text>
      </HStack>
    </Link>
  ) : null
}

type PathCardProgressProps = {
  progress?: number | null
} & BoxProps

// Used as a background overlay to indicate progress.
export function PathCardProgress({
  progress,
  ...boxProps
}: PathCardProgressProps) {
  return (
    <Box pos="absolute" inset={0} borderRadius="card" overflow="hidden">
      <Box
        pos="absolute"
        inset={0}
        right={`${100 - (progress ?? 0) * 100}%`}
        bg="whiteAlpha.300"
        transition="right 0.5s ease-out"
        {...boxProps}
      />
    </Box>
  )
}

export default PathCard
